<script setup lang="ts">
import { z } from 'zod'
import type { Form, FormSubmitEvent } from '#ui/types'
import { useAuthStore } from '~/store/auth'
import { useUserLotteryStore } from '~/store/scan/lottery'

defineOptions({
	name: 'ModalLoginTicket',
})

interface Props {
	modelValue?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: false,
})

const { t: $t, locale } = useI18n()
const { isReady, login } = useAuth()
const authStore = useAuthStore()
const userLotteryStore = useUserLotteryStore()
const route = useRoute()
const localePath = useLocalePath()

const emit = defineEmits(['update:modelValue', 'closeAuthGoogle'])

const schema = z.object({
	email: z.string().email($t('Email address is required')),
})

type Schema = z.output<typeof schema>

const state = reactive<Schema>({
	email: '',
})

const form = ref<Form<Schema>>()
const loading = ref<boolean>(false)

const labelInit = 'text-neutral-50 text-base font-semibold'

const labelClassRed = {
	base: labelInit,
	required: `after:content-['*'] after:ms-0.5 after:text-destructive-500`,
}

const isOpen = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
})

const isFormValid = computed(() => schema.safeParse(state).success)

const saveLastRoute = () => {
	const basePath = localePath('/results/scan')
	const queryString =
		'?lottery_id=2&regular_balls=3&regular_balls=4&regular_balls=8&regular_balls=20&regular_balls=31&country_id=6&page_number=1&extra_balls=6&extra_balls=8'
	saveLastInternalPath(`${basePath}${queryString}`)
}

const onSubmit = async (event: FormSubmitEvent<Schema>) => {
	form.value!.clear()
	loading.value = true
	saveLastRoute()
	const contentData = {
		email: event.data.email,
		lang: locale.value,
		redirect: route.fullPath,
	}

	try {
		const response = await authStore.login(contentData)
		loading.value = false

		if (response && !response?.success) {
			// Verificar que response.errors existe antes de usar Object.keys
			if (response.errors && typeof response.errors === 'object') {
				const errors = Object.keys(response.errors)
				form.value!.setErrors(
					errors.map((err) => ({
						message: response.errors[err][0],
						path: err,
					})),
				)
			} else {
				// Error genérico si no hay estructura de errores
				form.value!.setErrors([
					{
						message: $t('An unexpected error occurred. Please try again.'),
						path: 'email',
					},
				])
			}
			return
		}
	} catch {
		loading.value = false
		// Captura errores de red (timeout, etc.)
		form.value!.setErrors([
			{
				message: $t('Connection error. Please check your internet and try again.'),
				path: 'email',
			},
		])
		return
	}

	authStore.SET_STATE_AUTH({ type: 'email_to_verify', value: event.data.email })
	authStore.SET_STATE_AUTH({ type: 'type_modal_verify_email', value: 'login' })
	userLotteryStore.SET_STATE_LOTTERY({ type: 'showModalLogin', value: false })
	authStore.SET_STATE_AUTH({ type: 'showModalVerifyEmail', value: true })
}

const signUpGoogle = async () => {
	if (!isReady.value) return
	try {
		saveLastRoute()
		await login()
		userLotteryStore.SET_STATE_LOTTERY({
			type: 'showModalLogin',
			value: false,
		})
	} catch (error) {
		console.error('Sign up error:', error)
		clearLastInternalPath()
	} finally {
		emit('closeAuthGoogle')
	}
}

const openSignUp = () => {
	userLotteryStore.SET_STATE_LOTTERY({
		type: 'showModalLogin',
		value: false,
	})
	userLotteryStore.SET_STATE_LOTTERY({
		type: 'showModalSignUp',
		value: true,
	})
}
</script>

<template>
	<UModal
		v-model="isOpen"
		:transition="false"
		:ui="{
			overlay: {
				background: 'bg-[#040404D4] bg-opacity-80',
			},
			width: 'w-full sm:max-w-xl',
			container: 'items-center',
			padding: 'p-4 sm:p-4',
			background: 'bg-secondary-800 sm:bg-secondary-700',
			rounded: 'rounded-2xl',
		}"
	>
		<div class="rounded-2xl p-7 smm:p-12 w-full max-w-md sm:max-w-xl space-y-8 mx-auto">
			<!-- Title -->
			<div class="text-neutral-50 space-y-2.5 text-center">
				<h1 class="text-xl sm:text-3xl font-bold">{{ $t('Access the Ticket Scanner') }}</h1>

				<p class="mb-8 text-white">{{ $t('Sign in to access your tickets and prize history') }}?</p>
			</div>

			<UForm
				ref="form"
				:schema="schema"
				:state="state"
				class="space-y-6"
				@submit="onSubmit"
			>
				<!-- Email Field -->
				<UFormGroup
					name="email"
					:label="$t('Your mail address')"
					required
					:ui="{
						label: labelClassRed,
						error: 'text-destructive-800',
					}"
				>
					<UInput
						v-model="state.email"
						type="email"
						placeholder="youremail@email.com"
						color="primary"
						variant="outline"
						class="w-full"
						input-class="text-neutral-500 placeholder-neutral-800 ring-secondary-600 bg-secondary-900 focus:ring-1 min-h-10 text-base placeholder:text-sm"
						required
					/>
				</UFormGroup>

				<!-- Submit Button -->
				<UButton
					:disabled="!isFormValid"
					type="submit"
					:color="isFormValid ? 'primary' : 'white'"
					:variant="isFormValid ? 'solid' : 'soft'"
					:loading="loading"
					:label="$t('Sign in and scan ticket →')"
					class="rounded-full w-full py-2 text-base flex justify-center border-none"
					:class="
						isFormValid
							? 'bg-primary-500 hover:bg-primary-600 text-white'
							: 'text-neutral-700 cursor-not-allowed bg-white/[.08] hover:bg-white/[.10]'
					"
				/>
			</UForm>

			<p class="text-center text-neutral-50 text-sm mx-auto">
				{{ $t('Don’t have an account?') }}
				<button
					class="bg-gradient-green bg-clip-text text-transparent"
					@click="openSignUp"
				>
					{{ $t('Sign Up') }}
				</button>
			</p>

			<!-- Divider -->
			<div class="relative">
				<div class="absolute inset-0 flex items-center">
					<div class="w-full border-t border-[#040308]/[.4] smm:border-white/[.08]" />
				</div>
				<div class="relative flex justify-center text-sm">
					<span class="lowercase sm:capitalize px-4 bg-secondary-800 sm:bg-secondary-700 text-neutral-100">{{
						$t('Or')
					}}</span>
				</div>
			</div>

			<!-- Social Login -->
			<div class="text-neutral-100 text-center space-y-4">
				<p class="leading-6 text-sm sm:text-base">{{ $t('Login with') }}:</p>
				<UButton
					:disabled="!isReady"
					color="white"
					variant="soft"
					label="Google"
					icon="i-gg:google"
					class="rounded-full smm:rounded-lg w-full py-2.5 text-base flex justify-center bg-white/[.08] hover:bg-white/[.10] border border-white/[.06]"
					@click="signUpGoogle"
				/>
			</div>
		</div>
	</UModal>
</template>
